/**
 * Converged Extremes screener — stocks where ≥3 top-5% ia_skew funds
 * converged within 4Q, with specialist target prices and validated presets.
 */
import React, { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { ChevronDown, ChevronRight, RefreshCw, Star } from "lucide-react";
import { toast } from "sonner";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { API } from "./statsApi";

const CHAMPION = "top10_upside_nonhot_strong";

const fmtPct = (v, dp = 1) =>
  v == null ? "-" : `${v > 0 ? "+" : ""}${(v * 100).toFixed(dp)}%`;

const applyPreset = (rows, key) => {
  const nonhot = (r) => r.options_state !== "Hot";
  const quiet = (r) => r.options_state === "Quiet";
  const byUpside = (a, b) => (b.spec_upside ?? -9) - (a.spec_upside ?? -9);
  switch (key) {
    case "nonhot": return rows.filter(nonhot);
    case "quiet": return rows.filter(quiet);
    case "strong": return rows.filter((r) => r.strong);
    case "quiet_strong": return rows.filter((r) => quiet(r) && r.strong);
    case "nonhot_strong": return rows.filter((r) => nonhot(r) && r.strong);
    case "top10_upside_nonhot":
      return [...rows.filter(nonhot)].sort(byUpside).slice(0, 10);
    case CHAMPION:
      return [...rows.filter((r) => nonhot(r) && r.strong)].sort(byUpside).slice(0, 10);
    case "top10_upside_quiet":
      return [...rows.filter(quiet)].sort(byUpside).slice(0, 10);
    case "top10_upside_quiet_strong":
      return [...rows.filter((r) => quiet(r) && r.strong)].sort(byUpside).slice(0, 10);
    default: return rows;
  }
};

const staleDays = (r, asOf) =>
  r.target_as_of && asOf
    ? Math.round((new Date(asOf) - new Date(r.target_as_of)) / 86400000)
    : 0;

const BinaryRiskBadge = ({ r }) =>
  r.binary_risk ? (
    <Badge
      data-testid={`binary-risk-${r.symbol}`}
      title={`Binary-event risk: ATM IV ${r.atm_iv != null ? (r.atm_iv * 100).toFixed(0) + "%" : "n/a"} · expected move ${r.expected_move != null ? (r.expected_move * 100).toFixed(0) + "%" : "n/a"} — historical trap rate 15-19% in this cohort`}
      className="text-[9px] px-1 py-0 bg-amber-500/20 text-amber-400 border border-amber-500/40 cursor-help"
    >
      ⚠ binary
    </Badge>
  ) : null;

const StateChip = ({ state }) =>
  state ? (
    <Badge className={`text-[10px] border ${
      state === "Quiet" ? "bg-emerald-500/15 text-emerald-400 border-emerald-500/30"
        : state === "Hot" ? "bg-red-500/15 text-red-400 border-red-500/30"
        : "bg-muted text-muted-foreground border-border"}`}>
      {state}
    </Badge>
  ) : <span className="text-xs text-muted-foreground">—</span>;

const CapBadge = ({ bucket }) => (
  <Badge variant="outline" className="text-[9px] px-1 py-0">
    {bucket === "mid_small" ? "Mid/Sm" : "Big"}
  </Badge>
);

const Th = ({ children, right }) => (
  <th className={`py-1.5 px-2 font-medium text-muted-foreground whitespace-nowrap ${right ? "text-right" : "text-left"}`}>
    {children}
  </th>
);

const SpotlightCard = ({ r, rank, asOf, strongDef }) => {
  const stale = staleDays(r, asOf);
  return (
    <Card
      data-testid={`spotlight-${r.symbol}`}
      className="relative overflow-hidden border-indigo-500/25 bg-gradient-to-br from-indigo-500/[0.07] to-transparent"
    >
      <CardContent className="p-4 space-y-2.5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="text-xl font-bold tracking-tight">{r.symbol}</span>
            <CapBadge bucket={r.cap_bucket} />
            <BinaryRiskBadge r={r} />
          </div>
          <span className="text-[10px] font-mono text-muted-foreground">#{rank}</span>
        </div>
        <div className="flex items-end gap-3">
          <div>
            <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Spec upside</div>
            <div className={`text-2xl font-bold font-mono ${
              (r.spec_upside ?? 0) >= 0 ? "text-emerald-400" : "text-red-400"}`}>
              {fmtPct(r.spec_upside, 0)}
            </div>
          </div>
          <div className="text-xs font-mono text-muted-foreground pb-1">
            {r.price ?? "-"} → <span className="text-foreground">{r.spec_target ?? "-"}</span>
            {stale > 7 && (
              <span className="text-amber-400/80 ml-1" title={`Latest available analyst target is from ${r.target_as_of}`}>
                · tgt {r.target_as_of}
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap text-[11px]">
          <StateChip state={r.options_state} />
          {r.regime && (
            <span className="text-muted-foreground">
              {r.regime}
              {r.strong && <span className="text-emerald-400 ml-1 cursor-help" title={strongDef}>· strong</span>}
            </span>
          )}
          <span className="text-indigo-300 font-mono">{r.n_extreme} extreme funds</span>
        </div>
        <div className="text-[10px] text-muted-foreground truncate">
          {r.specialists?.map((s) => s.name).slice(0, 2).join(" · ") || ""}
        </div>
      </CardContent>
    </Card>
  );
};

export const ConvergedScreener = ({ token, dates }) => {
  const [selDate, setSelDate] = useState("");
  const [preset, setPreset] = useState(CHAMPION);
  const [cap, setCap] = useState("all");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    if (dates?.length && !selDate) setSelDate(dates[0]);
  }, [dates, selDate]);

  const load = useCallback(async (refresh = false) => {
    if (!selDate) return;
    setLoading(true);
    setExpanded(null);
    try {
      const res = await fetch(
        `${API}/stats/hidden-gems/converged?date=${selDate}${refresh ? "&refresh=true" : ""}`,
        { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error((await res.json()).detail || res.status);
      setData(await res.json());
    } catch (e) {
      toast.error(`Converged screener failed: ${e.message}`);
    } finally {
      setLoading(false);
    }
  }, [selDate, token]);

  useEffect(() => { load(); }, [load]);

  const rows = useMemo(() => {
    const base = cap === "all" ? (data?.rows || [])
      : (data?.rows || []).filter((r) => r.cap_bucket === cap);
    return applyPreset(base, preset);
  }, [data, preset, cap]);
  const presets = data?.validated?.presets || [];
  const strongDef = data?.validated?.strong_definition ||
    "Strong regime = nowcast cluster in one of the 5 historically bullish regimes: Momentum Lock-In, Compounding Drift, Delayed Inflection, Late Ignition, Slow Build Momentum.";
  const asOf = data?.resolved_date;

  return (
    <div className="space-y-5" data-testid="converged-screener">
      <div className="flex items-center gap-3 flex-wrap">
        <select
          data-testid="converged-date-select"
          className="h-9 w-40 rounded-md border border-border bg-background px-2 text-sm"
          value={selDate}
          onChange={(e) => setSelDate(e.target.value)}
        >
          {(dates || []).slice(0, 200).map((d) => (
            <option key={d} value={d}>{d}</option>
          ))}
        </select>
        <select
          data-testid="converged-cap-select"
          className="h-9 w-32 rounded-md border border-border bg-background px-2 text-sm"
          value={cap}
          onChange={(e) => setCap(e.target.value)}
        >
          <option value="all">All caps</option>
          <option value="big">Big</option>
          <option value="mid_small">Mid/Small</option>
        </select>
        <Button variant="outline" size="sm" disabled={loading} onClick={() => load(true)}
                data-testid="converged-refresh">
          <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${loading ? "animate-spin" : ""}`} />
          Recompute
        </Button>
        {data && (
          <span className="text-xs text-muted-foreground font-mono">
            as of {data.resolved_date}
            {data.options_as_of !== data.resolved_date ? ` · options ${data.options_as_of}` : ""} ·{" "}
            {data.universe?.converged} converged · {data.universe?.quiet} quiet ·{" "}
            {data.universe?.strong} strong {data.cached ? "· cached" : ""}
          </span>
        )}
      </div>

      <div className="flex gap-2 flex-wrap">
        {presets.map((p) => {
          const champ = p.key === CHAMPION;
          const active = preset === p.key;
          return (
            <button
              key={p.key}
              data-testid={`preset-${p.key}`}
              onClick={() => setPreset(p.key)}
              title={p.tooltip}
              className={`px-3 py-1.5 rounded-md border text-left transition-colors ${
                active
                  ? champ
                    ? "border-emerald-500/60 bg-emerald-500/15 text-emerald-300"
                    : "border-indigo-500/50 bg-indigo-500/15 text-indigo-300"
                  : champ
                    ? "border-emerald-500/30 text-emerald-400/80 hover:bg-emerald-500/10"
                    : "border-border text-muted-foreground hover:bg-muted"
              }`}
            >
              <div className="text-xs font-semibold flex items-center gap-1">
                {champ && <Star className="w-3 h-3 fill-current" />}
                {p.label.replace(" ⭐", "")}
              </div>
              <div className="text-[10px] font-mono opacity-70">{p.stats}</div>
            </button>
          );
        })}
      </div>

      {loading && !data ? (
        <div className="py-16 text-center text-sm text-muted-foreground">Computing…</div>
      ) : (
        <>
          {rows.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="converged-spotlight">
              {rows.slice(0, 3).map((r, i) => (
                <SpotlightCard key={r.symbol} r={r} rank={i + 1} asOf={asOf} strongDef={strongDef} />
              ))}
            </div>
          )}

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">
                {rows.length} names — {presets.find((p) => p.key === preset)?.label || preset}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-xs" data-testid="converged-table">
                  <thead className="border-b border-border">
                    <tr>
                      <Th>#</Th><Th>Symbol</Th><Th right>Extreme funds</Th>
                      <Th right>Mean skew</Th><Th right>Spec target</Th>
                      <Th right>Analyst T</Th><Th right>Gradient</Th><Th>State</Th>
                      <Th right>µα 3M</Th><Th>Regime</Th><Th right>Price</Th>
                      <Th>Top specialists</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r, i) => {
                      const stale = staleDays(r, asOf);
                      return (
                        <Fragment key={r.symbol}>
                          <tr
                            data-testid={`converged-row-${r.symbol}`}
                            onClick={() => setExpanded(expanded === r.symbol ? null : r.symbol)}
                            className="border-b border-border/40 cursor-pointer hover:bg-muted/40"
                          >
                            <td className="py-1.5 px-2 text-muted-foreground">
                              <span className="inline-flex items-center gap-1">
                                {expanded === r.symbol
                                  ? <ChevronDown className="w-3 h-3" />
                                  : <ChevronRight className="w-3 h-3" />}
                                {i + 1}
                              </span>
                            </td>
                            <td className="py-1.5 px-2 font-semibold whitespace-nowrap">
                              {r.symbol} <CapBadge bucket={r.cap_bucket} /> <BinaryRiskBadge r={r} />
                            </td>
                            <td className="py-1.5 px-2 text-right font-mono text-indigo-300 font-semibold">
                              {r.n_extreme}
                            </td>
                            <td className="py-1.5 px-2 text-right font-mono">{r.mean_skew}</td>
                            <td className="py-1.5 px-2 text-right font-mono">
                              {r.spec_target == null ? (
                                <span className="text-muted-foreground" title="No analyst target coverage in the last 120 days">—</span>
                              ) : (
                                <>
                                  <div>
                                    {r.spec_target}
                                    {stale > 7 && (
                                      <span className="text-amber-400/80 text-[9px] ml-1"
                                            title={`Latest available analyst target is from ${r.target_as_of}`}>
                                        {r.target_as_of}
                                      </span>
                                    )}
                                  </div>
                                  {r.spec_upside != null && (
                                    <div className={r.spec_upside >= 0 ? "text-green-400" : "text-red-400"}>
                                      {fmtPct(r.spec_upside, 0)}
                                    </div>
                                  )}
                                </>
                              )}
                            </td>
                            <td className="py-1.5 px-2 text-right font-mono text-muted-foreground">
                              <div>{r.analyst_target ?? "-"}</div>
                              {r.analyst_upside != null && <div>{fmtPct(r.analyst_upside, 0)}</div>}
                            </td>
                            <td className={`py-1.5 px-2 text-right font-mono ${
                              r.gradient > 0 ? "text-green-400" : r.gradient < 0 ? "text-red-400" : "text-muted-foreground"}`}>
                              {r.gradient != null ? fmtPct(r.gradient) : "-"}
                            </td>
                            <td className="py-1.5 px-2"><StateChip state={r.options_state} /></td>
                            <td className={`py-1.5 px-2 text-right font-mono ${
                              r.micro_alpha_3m > 0 ? "text-green-400" : r.micro_alpha_3m < 0 ? "text-red-400" : "text-muted-foreground"}`}>
                              {r.micro_alpha_3m != null ? `${r.micro_alpha_3m > 0 ? "+" : ""}${r.micro_alpha_3m}%` : "-"}
                            </td>
                            <td className="py-1.5 px-2 whitespace-nowrap">
                              {r.regime || "-"}{" "}
                              {r.strong && (
                                <Badge title={strongDef}
                                       className="text-[9px] bg-emerald-500/15 text-emerald-400 border border-emerald-500/30 ml-1 cursor-help">
                                  strong
                                </Badge>
                              )}
                            </td>
                            <td className="py-1.5 px-2 text-right font-mono">{r.price ?? "-"}</td>
                            <td className="py-1.5 px-2 text-muted-foreground max-w-[200px] truncate">
                              {r.specialists?.map((s) => s.name).slice(0, 2).join(", ") || "—"}
                            </td>
                          </tr>
                          {expanded === r.symbol && (
                            <tr className="bg-muted/20 border-b border-border/40">
                              <td colSpan={12} className="p-3">
                                <div className="text-[11px] font-semibold mb-1.5 text-muted-foreground uppercase">
                                  Extreme specialists (top-5% sector ia_skew, bought within 4Q)
                                </div>
                                <table className="w-full text-[11px] max-w-2xl">
                                  <tbody>
                                    {(r.specialists || []).map((s, j) => (
                                      <tr key={j} className="border-b border-border/30">
                                        <td className="py-1 pr-2">{s.name}</td>
                                        <td className="py-1 pr-2 font-mono text-right">skew {s.ia_skew}</td>
                                        <td className="py-1 text-muted-foreground">{s.bought}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                                <div className="text-[11px] font-mono mt-2 text-muted-foreground">
                                  Spec target = analyst median × (1 + specialist curve gradient − sector-normalized alpha),
                                  clipped to analyst range · conviction {r.conviction_score != null
                                    ? `${(r.conviction_score * 100).toFixed(0)} (${["Overcrowded Peak", "Distribution Phase"].includes(r.regime) ? "P underperform" : "P deliver"})`
                                    : "-"} ·
                                  nowcast grad 3M {fmtPct(r.grad_3m)}
                                </div>
                              </td>
                            </tr>
                          )}
                        </Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          <p className="text-[11px] text-muted-foreground font-mono">
            {data?.validated?.definition || "≥3 top-5% ia_skew funds converged within 4Q"} ·
            Backtest {data?.validated?.window || "2024-08 → 2026-07"}
          </p>
        </>
      )}
    </div>
  );
};
